<script setup lang="ts">
import { computed, type Component } from 'vue'
import AppBadge from '../../primitives/AppBadge/AppBadge.vue'
import AppTooltip from '../../primitives/AppTooltip/AppTooltip.vue'
import type { NavBadge } from './nav-types'
import { useSidebar } from './useSidebar'

const ROW_ARRIVAL_DELAY_MS = 40
const ROW_ARRIVAL_STAGGER_MS = 35

const props = defineProps<{
  label: string
  icon: Component
  active?: boolean
  /** Optional count badge shown on the item. */
  badge?: NavBadge
  /** Position in its group; staggers the row's arrival when the group unfolds. */
  index?: number
}>()
defineEmits<{ select: [] }>()

const { state } = useSidebar()
const rail = computed(() => state.value === 'rail')

const arrivalDelay = computed(
  () => `${ROW_ARRIVAL_DELAY_MS + (props.index ?? 0) * ROW_ARRIVAL_STAGGER_MS}ms`,
)
</script>

<template>
  <AppTooltip :content="rail ? label : ''" placement="right">
    <template #trigger="{ aria }">
      <button
        type="button"
        data-arrival="nav-row"
        :aria-label="label"
        :aria-current="active ? 'page' : undefined"
        :style="{ animationDelay: arrivalDelay }"
        v-bind="aria"
        class="group relative flex h-16 w-full select-none items-center rounded-4 text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue"
        :class="[
          rail ? 'justify-center' : 'gap-4 px-4',
          active ? 'text-body-sm-medium' : 'text-body-sm hover:bg-bg-wash',
        ]"
        @click="$emit('select')"
      >
        <component :is="icon" class="shrink-0 text-icon-primary" aria-hidden="true" />
        <span v-if="!rail" class="min-w-0 flex-1 truncate text-left">{{ label }}</span>
        <AppBadge
          v-if="badge && badge.count > 0"
          :tone="badge.tone ?? 'blue'"
          class="shrink-0"
          :class="rail ? 'absolute -right-2 -top-2' : 'ml-auto'"
        >
          {{ badge.count }}
        </AppBadge>
      </button>
    </template>
  </AppTooltip>
</template>
